import { Component, forwardRef, inject } from '@angular/core';
import { Languages } from '../../enums/languages.enum';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Constants } from '../../constants';

@Component({
    selector: 'app-language-switcher',
    templateUrl: './language-switcher.component.html',
    styleUrl: './language-switcher.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LanguageSwitcherComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class LanguageSwitcherComponent {
  private translateService = inject(TranslateService);

  public readonly languages = Languages;
  public readonly supportedLanguages = Constants.SupportedLanguages;

  public isOpen$ = new BehaviorSubject(false);
  public selectedLanguage$ = new BehaviorSubject(Languages[Constants.DefaultLanguage as keyof typeof Languages]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: Languages) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    this.applyLanguage(this.selectedLanguage$.getValue());
  }

  public toggleDropdown() {
    this.isOpen$.next(!this.isOpen$.value);
  }

  writeValue(value: Languages): void {
    if (value === null || value === undefined) {
      return;
    }

    this.selectedLanguage$.next(value);
    this.applyLanguage(value);
  }

  registerOnChange(fn: (value: Languages) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public selectLanguage(value: Languages): void {
    this.selectedLanguage$.next(value);
    this.onChange(value);
    this.onTouched();

    this.applyLanguage(value);
    this.isOpen$.next(false);
  }

  private applyLanguage(value: Languages): void {
    const languageKey = Languages[value];

    if (!languageKey) {
      return;
    }

    window.localStorage.setItem('interfaceLanguage', languageKey);
    this.translateService.use(languageKey);
  }
}
